// Vendor Libs
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

// Dish Tech React
import {InputTextField, Overlay} from '@dishtech-react/core';
import {useForm, useToast, useOverlay} from '@dishtech-react/hooks';

// Material UI
import CancelIcon from '@material-ui/icons/Cancel';
import SaveIcon from '@material-ui/icons/Save';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import {Button, FormControlLabel, Switch, Typography, Chip, Radio, RadioGroup, FormControl} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import ChipInput from 'material-ui-chip-input';

// Components
import axios from 'axios';
import MultiImagePicker from '../helpers/multi-image-picker';

// Configs
import {serviceConfig} from '../../../config/service-config';

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(2, 1),
  },
  border: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  dropzone: {
    marginTop: theme.spacing(2),
  },
  message: {
    display: 'flex',
    '& div': {
      color: '#838383',
      marginLeft: '1em',
    },
  },
  vcaChips: {
    margin: theme.spacing(0, 0.25),
  },
}));

const FORM_KEY = 'NETWORK';

const NetworkForm = ({mode, onCancel, onSave}) => {
  const classes = useStyles();
  const {
    formValid,
    formValues,
    formValues: {
      description,
      displayName,
      name,
      offerToNonSubscribers = false,
      pictureIdEnabled = true,
      premium = false,
      providers = [],
      standardImageURL,
      widescreenImageURL,
      widescreenBackgroundImageURL,
      tags = [],
    },
    registerForm,
    resetForm,
    setFieldValue,
    submitForm,
  } = useForm(serviceConfig, FORM_KEY, mode);
  const [validProviderId, setValidProviderId] = useState(true);
  const {createToast} = useToast();
  const [currentChip, setSelectedChip] = useState({});
  const [isUpdating, setUpdating] = useState(false);

  useEffect(() => {
    registerForm();
    return () => {
      resetForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteChip = (key, idx) => {
    setFieldValue(
      key,
      formValues[key].filter((_value, index) => index !== idx)
    );
  };

  const handleSave = async () => {
    const removeByteImagesFromNetworkDTO = network =>
      Object.keys(network).reduce(
        (accum, key) => (!key.toLowerCase().includes('imagebytes') ? {...accum, [key]: network[key]} : accum),
        {}
      );

    const handleKafkaNetworkUpdate = network => {
      const {
        NETWORK_KAFKA_UPDATES: {path, baseUrl, method},
      } = serviceConfig;

      return axios[method.toLowerCase()](`${baseUrl}${path}`, {
        messages: [
          {
            value: JSON.stringify({
              status: `${mode}d`,
              network,
            }),
          },
        ],
      });
    };

    const networkWithNoByteImage = removeByteImagesFromNetworkDTO(formValues);

    setUpdating(true);
    // Kafka can't sent big files as bytes.
    await handleKafkaNetworkUpdate(networkWithNoByteImage)
      .then(({error}) => {
        if (error) {
          createToast(error, {
            variant: 'error',
            persist: true,
          });
        }
      })
      .then(() => submitForm(formValues))
      .then(() => {
        createToast(`Successfully ${mode}d network.`);
        onSave();
      })
      .catch(error => {
        createToast(error.message, {
          variant: 'error',
          persist: true,
        });
      });
    setUpdating(false);
  };

  const onDropHandler = (fieldname, filename) => {
    const reader = new FileReader();
    reader.readAsDataURL(filename);
    reader.onload = event => {
      const imageBytes = event.target.result.split(',')[1];
      setFieldValue(fieldname, imageBytes);
    };
    reader.onerror = event => {
      const errorCode = event.target.error.code;
      console.error(`Could not read the file. Encountered the following error code: ${errorCode}`);
    };
  };

  const {
    closeOverlay: closeChipOverlay,
    open: isChipOverlayOpen,
    openOverlay: openChipOverlay,
    overlayId: chipOverlayId,
  } = useOverlay({
    title: 'vca-chip',
  });

  const handleClickChip = chip => {
    providers.forEach(provider => {
      if (chip.providerID === provider.providerID) {
        openChipOverlay();
        setSelectedChip(chip);
      }
    });
  };

  // Have to create a new array because providers with adFree: null values are not returned in the response from server.
  // Front-end needs to insert an adFree: null key/value so radio group can properly read the value
  const formattedProviders = [...providers].map(provider => ({
    providerID: provider.providerID,
    adFree: provider.adFree ? provider.adFree : provider.adFree === false ? false : null,
  }));

  const handleUpdatedChip = async () => {
    const updatedProvidersList = formattedProviders.map(provider =>
      provider.providerID !== currentChip.providerID ? provider : currentChip
    );

    setFieldValue('providers', [...updatedProvidersList]);

    registerForm();
    closeChipOverlay();
  };

  const handleCheck = adFreeStatus => {
    const chipAdStatus = {
      ...providers.find(provider => provider.providerID === currentChip.providerID),
      adFree:
        adFreeStatus === 'adFree'
          ? true
          : adFreeStatus === 'adSupported'
          ? false
          : adFreeStatus === ''
          ? null
          : currentChip.adFree,
    };

    setSelectedChip(chipAdStatus);
  };

  return (
    <>
      <InputTextField
        autoFocus={true}
        className={classes.border}
        disabled={mode !== 'create'}
        fullWidth={true}
        id="name"
        label={`Name${mode === 'create' ? ' (required)' : ''}`}
        margin="dense"
        type="text"
        value={name || ''}
        onChange={event => setFieldValue('name', event.target.value)}
      />
      <InputTextField
        autoFocus={false}
        className={classes.border}
        fullWidth={true}
        id="displayName"
        label="Display Name"
        margin="dense"
        type="text"
        value={displayName || ''}
        onChange={event => setFieldValue('displayName', event.target.value)}
      />
      <InputTextField
        autoFocus={false}
        className={classes.border}
        fullWidth={true}
        id="description"
        label="Description"
        margin="dense"
        multiline={true}
        type="text"
        value={description || ''}
        onChange={event => setFieldValue('description', event.target.value)}
      />
      <ChipInput
        className={classes.border}
        fullWidth={true}
        id="tags"
        label={
          <Typography component="span">
            <div className={classes.message}>
              Network Tag(s)
              <div>
                <i>Press Enter for each network</i>
              </div>
            </div>
          </Typography>
        }
        value={tags || []}
        onAdd={chip => setFieldValue('tags', [...tags, chip])}
        onDelete={(_chip, index) => handleDeleteChip('tags', index)}
      />
      <ChipInput
        chipRenderer={({value, handleDelete}) => (
          <Chip
            className={classes.vcaChips}
            icon={<ArrowForwardIcon fontSize="small" />}
            key={value.providerID}
            label={value.providerID}
            onClick={() => handleClickChip(value)}
            onDelete={handleDelete}
          />
        )}
        className={classes.border}
        fullWidth={true}
        id="providers"
        label={
          <Typography component="span">
            <div className={classes.message}>
              VCA Provider(s){' '}
              {validProviderId ? (
                <div>
                  <i>Press Enter for each provider</i>
                </div>
              ) : (
                <div style={{color: 'red'}}>
                  <i>Cannot be more than 4 characters or duplicate found</i>
                </div>
              )}
            </div>
          </Typography>
        }
        value={formattedProviders || []}
        onAdd={chip => {
          const chipObj = {providerID: chip, adFree: null};
          setFieldValue('providers', [...providers, chipObj]);
        }}
        onBeforeAdd={chip => {
          const searchDuplicate = providers.some(el => el.providerID === chip);
          const valid = chip.length < 5 && !searchDuplicate;
          setValidProviderId(valid);
          return valid;
        }}
        onDelete={(_chip, index) => handleDeleteChip('providers', index)}
      />
      <Overlay id={chipOverlayId} open={isChipOverlayOpen} title="Ad Status">
        <FormControl>
          <RadioGroup onChange={(_evt, value) => handleCheck(value)}>
            <FormControlLabel
              control={<Radio checked={!!currentChip.adFree} color="primary" value="adFree" />}
              label="Ad-free"
            />
            <FormControlLabel
              control={
                <Radio
                  checked={currentChip.adFree === false && currentChip.adFree !== null}
                  color="primary"
                  value="adSupported"
                />
              }
              label="Ad-supported"
            />
            <FormControlLabel
              control={<Radio checked={currentChip.adFree === null} color="primary" value="" />}
              label="N/A"
            />
          </RadioGroup>
          <Button
            className={classes.button}
            color="default"
            startIcon={<CancelIcon />}
            variant="contained"
            onClick={closeChipOverlay}
          >
            Cancel
          </Button>
          <Button
            className={classes.button}
            color="primary"
            startIcon={<SaveIcon />}
            variant="contained"
            onClick={handleUpdatedChip}
          >
            update
          </Button>
        </FormControl>
      </Overlay>
      <div className={classes.border}>
        <FormControlLabel
          control={
            <Switch
              checked={premium}
              color="primary"
              id="premium"
              value={premium}
              onChange={event => setFieldValue('premium', event.target.checked)}
            />
          }
          label={`${premium ? '' : 'Not'} Premium`}
        />
      </div>
      <div className={classes.border}>
        <FormControlLabel
          control={
            <Switch
              checked={offerToNonSubscribers}
              color="primary"
              id="offerToNonSubscribers"
              value={offerToNonSubscribers}
              onChange={event => setFieldValue('offerToNonSubscribers', event.target.checked)}
            />
          }
          label={`${offerToNonSubscribers ? 'Offered' : 'Not offered'} to non-subscribers`}
        />
      </div>
      <div className={classes.border}>
        <FormControlLabel
          control={
            <Switch
              checked={pictureIdEnabled}
              color="primary"
              id="pictureIdEnabled"
              value={pictureIdEnabled}
              onChange={event => setFieldValue('pictureIdEnabled', event.target.checked)}
            />
          }
          label={`${pictureIdEnabled ? 'Use most watched content image' : 'Use default image'} `}
        />
      </div>
      <MultiImagePicker
        firstDisplayName="Standard"
        firstDisplayType="standardImage"
        firstImageRequire={true}
        firstImageURL={standardImageURL}
        secondDisplayName="Widescreen"
        secondDisplayType="widescreenImage"
        secondImageRequire={true}
        secondImageURL={widescreenImageURL}
        thirdDisplayName="Widescreen Background"
        thirdDisplayType="widescreenBackgroundImage"
        thirdImageRequire={false}
        thirdImageURL={widescreenBackgroundImageURL}
        onAdd={(fieldName, filename) => onDropHandler(fieldName, filename)}
        onDelete={fieldName => {
          const validUrls = [
            'standardImageBytes',
            'widescreenBackgroundImageBytes',
            'widescreenBackgroundImageURL',
            'widescreenImageBytes',
          ];
          if (validUrls.includes(fieldName)) {
            setFieldValue(fieldName, '');
          }
        }}
      />
      <Button
        className={classes.button}
        color="default"
        startIcon={<CancelIcon />}
        variant="contained"
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        className={classes.button}
        color="primary"
        disabled={!formValid || isUpdating}
        startIcon={<SaveIcon />}
        variant="contained"
        onClick={handleSave}
      >
        {isUpdating ? `${mode.slice(0, -1)}ing` : mode}
      </Button>
    </>
  );
};

NetworkForm.propTypes = {
  mode: PropTypes.string,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
};

NetworkForm.defaultProps = {
  mode: 'create',
};

export default NetworkForm;
